import React, { useState } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import {
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  Switch,
  FormControlLabel,
} from '@mui/material';

function getAuthHeaders() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (!sessionToken) return {};
  return { Authorization: `Bearer ${sessionToken}` };
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function SendNotification() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Talk Page fields
  const [talkTitle, setTalkTitle] = useState('');
  const [talkMessage, setTalkMessage] = useState('');
  const [talkId, setTalkId] = useState('');
  const [talkMatchId, setTalkMatchId] = useState('');
  const [talkTopicRowId, setTalkTopicRowId] = useState('');
  const [talkIsJoined, setTalkIsJoined] = useState(false);
  const [talkPlayerId, setTalkPlayerId] = useState('');

  // Around the Sport fields
  const [atsTitle, setAtsTitle] = useState('');
  const [atsMessage, setAtsMessage] = useState('');
  const [atsOpinionId, setAtsOpinionId] = useState('');
  const [atsTopicName, setAtsTopicName] = useState('');
  const [atsImageUrl, setAtsImageUrl] = useState('');
  const [atsReactionsCount, setAtsReactionsCount] = useState('0');
  const [atsCid, setAtsCid] = useState('0');
  const [atsActive, setAtsActive] = useState(true);

  // Deep link preview
  const talkDeepLink = talkId
    ? `dugout://talk/${encodeURIComponent(talkId)}?match_id=${talkMatchId}&topic_row_id=${talkTopicRowId}&is_joined=${talkIsJoined ? '1' : '0'}${talkPlayerId ? `&player_id=${talkPlayerId}` : ''}`
    : '';

  const atsDeepLink = atsOpinionId
    ? `dugout://opinion_feeds/${atsOpinionId}?section=around_the_sport&opinionData=${encodeURIComponent(JSON.stringify({ id: Number(atsOpinionId), topic_name: atsTopicName, image_url: atsImageUrl, reactions_count: Number(atsReactionsCount), cid: Number(atsCid), created_at: '', updated_at: '', active: atsActive }))}`
    : '';

  const handleSendTalk = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!talkTitle.trim() || !talkMessage.trim() || !talkId.trim() || !talkMatchId.trim() || !talkTopicRowId.trim()) {
      setError('Title, Message, ID, Match ID, and Topic Row ID are required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/notifications/talk-page`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          title: talkTitle.trim(),
          message: talkMessage.trim(),
          id: talkId.trim(),
          match_id: Number(talkMatchId),
          topic_row_id: Number(talkTopicRowId),
          is_joined: talkIsJoined ? '1' : '0',
          player_id: talkPlayerId.trim() ? Number(talkPlayerId) : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Failed to send notification');
        return;
      }
      setSuccess('Talk page notification sent to all users.');
    } catch (err) {
      setError('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAts = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!atsTitle.trim() || !atsMessage.trim() || !atsOpinionId.trim()) {
      setError('Title, Message, and Opinion ID are required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/admin/notifications/around-the-sport`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          title: atsTitle.trim(),
          message: atsMessage.trim(),
          opinionData: {
            id: Number(atsOpinionId),
            topic_name: atsTopicName.trim(),
            image_url: atsImageUrl.trim(),
            reactions_count: Number(atsReactionsCount) || 0,
            cid: Number(atsCid) || 0,
            created_at: '',
            updated_at: '',
            active: atsActive,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Failed to send notification');
        return;
      }
      setSuccess('Around the Sport notification sent to all users.');
    } catch (err) {
      setError('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>Send Push Notification</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

              <Card>
                <CardContent>
                  <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Talk Page" />
                    <Tab label="Around the Sport" />
                  </Tabs>

                  {/* Talk Page Tab */}
                  <TabPanel value={tab} index={0}>
                    <form onSubmit={handleSendTalk}>
                      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
                        <Grid item xs={12}>
                          <TextField fullWidth required label="Notification Title" value={talkTitle} onChange={(e) => setTalkTitle(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth required label="Notification Message" value={talkMessage} onChange={(e) => setTalkMessage(e.target.value)} multiline rows={2} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth required label="Talk ID (path slug)" value={talkId} onChange={(e) => setTalkId(e.target.value)} helperText="e.g. virat-vs-bumrah" />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth required label="Match ID" type="number" value={talkMatchId} onChange={(e) => setTalkMatchId(e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth required label="Topic Row ID" type="number" value={talkTopicRowId} onChange={(e) => setTalkTopicRowId(e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Switch checked={talkIsJoined} onChange={(e) => setTalkIsJoined(e.target.checked)} />}
                            label={`is_joined: ${talkIsJoined ? '1' : '0'}`}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth label="Player ID (optional)" type="number" value={talkPlayerId} onChange={(e) => setTalkPlayerId(e.target.value)} />
                        </Grid>
                        {talkDeepLink && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              Deep Link: {talkDeepLink}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Button type="submit" variant="contained" color="primary" disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                            {loading ? 'Sending...' : 'Send to All Users'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </TabPanel>

                  {/* Around the Sport Tab */}
                  <TabPanel value={tab} index={1}>
                    <form onSubmit={handleSendAts}>
                      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
                        <Grid item xs={12}>
                          <TextField fullWidth required label="Notification Title" value={atsTitle} onChange={(e) => setAtsTitle(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth required label="Notification Message" value={atsMessage} onChange={(e) => setAtsMessage(e.target.value)} multiline rows={2} />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 1 }}>Opinion Data</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth required label="Opinion ID" type="number" value={atsOpinionId} onChange={(e) => setAtsOpinionId(e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField fullWidth label="Topic Name" value={atsTopicName} onChange={(e) => setAtsTopicName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label="Image URL" value={atsImageUrl} onChange={(e) => setAtsImageUrl(e.target.value)} placeholder="https://..." />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField fullWidth label="Reactions Count" type="number" value={atsReactionsCount} onChange={(e) => setAtsReactionsCount(e.target.value)} />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField fullWidth label="CID" type="number" value={atsCid} onChange={(e) => setAtsCid(e.target.value)} />
                        </Grid>
                        <Grid item xs={4}>
                          <FormControlLabel
                            control={<Switch checked={atsActive} onChange={(e) => setAtsActive(e.target.checked)} />}
                            label={atsActive ? 'Active' : 'Inactive'}
                          />
                        </Grid>
                        {atsDeepLink && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              Deep Link: {atsDeepLink}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Button type="submit" variant="contained" color="primary" disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
                            {loading ? 'Sending...' : 'Send to All Users'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </TabPanel>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default SendNotification;
